import { Helmet } from "react-helmet-async";
import { COMPANY } from "@/config/company";

interface TreatmentServiceJsonLdProps {
  name: string;
  description: string;
  category: string;
  priceIndia?: { min: number; max: number };
  priceUSA?: { min: number; max: number };
  url: string;
  imageUrl?: string;
}

/**
 * Service/MedicalProcedure schema for individual treatment pages.
 * Provides structured data for each medical treatment offered.
 */
export function TreatmentServiceJsonLd({
  name,
  description,
  category,
  priceIndia,
  priceUSA,
  url,
  imageUrl,
}: TreatmentServiceJsonLdProps) {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalProcedure",
    name,
    description,
    procedureType: "https://schema.org/SurgicalProcedure",
    url,
    ...(imageUrl && { image: imageUrl }),
    provider: {
      "@type": "MedicalBusiness",
      "@id": `${COMPANY.website}/#medicalbusiness`,
      name: COMPANY.name,
      url: COMPANY.website,
    },
    areaServed: ["United States", "United Kingdom", "Canada", "Australia", "Middle East"],
    availableChannel: {
      "@type": "ServiceChannel",
      serviceUrl: `${COMPANY.website}/contact`,
      servicePhone: COMPANY.phone.raw,
      serviceSmsNumber: COMPANY.whatsapp.number,
    },
  };

  // Add price comparison as offers if available
  const offersSchema = priceIndia
    ? {
        "@context": "https://schema.org",
        "@type": "Offer",
        name: `${name} in India via TrustedHeal`,
        description: `${name} at JCI-accredited hospitals in India. Save ${priceUSA ? `up to ${Math.round(((priceUSA.max - priceIndia.min) / priceUSA.max) * 100)}%` : "30-70%"} compared to USA prices.`,
        url,
        priceCurrency: "USD",
        price: priceIndia.min,
        priceSpecification: {
          "@type": "PriceSpecification",
          priceCurrency: "USD",
          minPrice: priceIndia.min,
          maxPrice: priceIndia.max,
        },
        availability: "https://schema.org/InStock",
        validFrom: new Date().toISOString().split("T")[0],
        offeredBy: {
          "@type": "MedicalBusiness",
          "@id": `${COMPANY.website}/#medicalbusiness`,
        },
      }
    : null;

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      {offersSchema && (
        <script type="application/ld+json">{JSON.stringify(offersSchema)}</script>
      )}
    </Helmet>
  );
}
